/*
 * /colreg-lights — огни, знаки и сигналы МППСС-72.
 *
 * Четыре режима: тренажёр (ночная сцена + вопрос), экзамен (20 вопросов без
 * подсказок и на время), справочник (любое судно с любого ракурса) и сигналы.
 *
 * Кадр перерисовывается по requestAnimationFrame ТОЛЬКО когда в кадре есть
 * проблесковый огонь, идёт вращение или перетаскивание. Постоянный цикл на
 * статичной картинке — бесконечный тик, который в проекте измеряли и запрещали.
 *
 * 2026-09-13:
 *  · раскладка на общем каркасе: сцена и вопрос на сцене, настройки и счёт в
 *    рейке; на ноутбуке вопрос и варианты видны без прокрутки;
 *  · расхождение — из геометрии встречи (quiz.js) и с вариантом «опасности нет»;
 *  · вопрос «по звуку в тумане»;
 *  · видимость — ползунок, тренирует ограниченную видимость;
 *  · сцену справочника можно крутить пальцем или мышью;
 *  · экзамен и печатные карточки (карточки — Премиум).
 */

import { CONFIGS, BY_ID, SIGNALS, whistleHz } from './catalogue.js';
import { draw, drawPlan, visibleLights, horizonNm, autoFov } from './scene.js';
import { makeQuestion, makeExam, explain, loadSkill, saveSkill, noteAnswer, giveWay, SOUND_CLASSES } from './quiz.js';
import { play as playSignal, unlock as unlockAudio } from './sound.js';
import { makeT } from '../maritime/i18n.js';

const NM = 1852;
const B = window.__COLREG || { T: {}, V: {}, R: {}, S: {}, cfg: {} };
const T = B.T, V = B.V, RULES = B.R, SIG = B.S;
const t = makeT(T);
const $ = id => document.getElementById(id);
const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
const isPro = () => !!(window.__isProNow && window.__isProNow(false));
const store = {
    load: (k, d) => { try { return window.__toolSettings?.load('colreg-lights', k, d) ?? d; } catch { return d; } },
    save: (k, v) => { try { window.__toolSettings?.save('colreg-lights', k, v); } catch { } },
};
const funnel = (ev, d) => { try { window.__aeFunnel?.(ev, d || {}); } catch { } };

/* ── состояние ───────────────────────────────────────────────────────────── */
const S = {
    mode: 'train',
    level: 1,
    types: ['identify'],
    night: true,
    silhouette: false,
    metVis: 10,
    q: null,
    answered: false,
    stats: { asked: 0, right: 0, streak: 0, best: 0 },
    skill: {},
    ref: { id: CONFIGS[0].id, aspect: 35, distNm: 1.5, eye: 12, spin: false },
    exam: null,
    raf: 0,
    audio: null,
    drag: null,
};

/* ── общий цикл отрисовки ────────────────────────────────────────────────── */
let needsAnim = false;
function frame() {
    S.raf = 0;
    if ((S.mode === 'train' || S.mode === 'exam') && S.q && S.q.config) paintScene($('clScene'), S.q.config, S.q.view, S.q.night);
    if (S.mode === 'ref') {
        if (S.ref.spin && !S.drag) { S.ref.aspect = ((S.ref.aspect + 0.45 + 180) % 360) - 180; syncRefAspect(); }
        const cfg = BY_ID[S.ref.id];
        paintScene($('clRefScene'), cfg, refView(), S.night);
        drawPlan($('clRefPlan'), cfg, refView());
    }
    if (needsAnim && !document.hidden) S.raf = requestAnimationFrame(frame);
}
function kick(anim) {
    needsAnim = !!anim;
    if (!S.raf) S.raf = requestAnimationFrame(frame);
}
const hasFlash = cfg => !!cfg && cfg.lights.some(l => l.flash);

function paintScene(canvas, cfg, view, night) {
    if (!canvas || !canvas.clientWidth) return;
    draw(canvas, cfg, view, { night, silhouette: S.silhouette, metVis: S.metVis, timeMs: performance.now() });
}
document.addEventListener('visibilitychange', () => { if (!document.hidden && needsAnim) kick(true); });

/* ── режимы ──────────────────────────────────────────────────────────────── */
function setMode(m) {
    if (S.mode === 'exam' && m !== 'exam' && S.exam && !S.exam.done && !window.confirm(t('exam_leave'))) return;
    S.mode = m;
    for (const b of document.querySelectorAll('.cl-mode')) { const on = b.dataset.mode === m; b.classList.toggle('on', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); }
    const quizPane = m === 'train' || m === 'exam';
    for (const p of document.querySelectorAll('.cl-pane')) p.hidden = p.dataset.mode !== (quizPane ? 'train' : m);
    $('clTrainRail').hidden = m !== 'train';
    $('clExamRail').hidden = m !== 'exam';
    if (m === 'train') { S.exam = null; nextQuestion(); }
    if (m === 'exam') startExam();
    if (m === 'ref') renderRef();
    if (m === 'sig') renderSignals();
    kick(quizPane ? hasFlash(S.q?.config) : m === 'ref' ? (S.ref.spin || hasFlash(BY_ID[S.ref.id])) : false);
    window.__stage?.('mode_' + m);
}

/* ═══ ТРЕНАЖЁР ═══════════════════════════════════════════════════════════ */
function nextQuestion() {
    S.q = makeQuestion({ level: S.level, types: S.types, night: S.night, metVis: S.metVis, skill: S.skill });
    showQuestion();
}

function showQuestion() {
    S.answered = false;
    $('clVerdict').hidden = true;
    $('clNext').hidden = true;
    const q = S.q;
    const isSound = q.type === 'sound';
    $('clStageBox').classList.toggle('fog', isSound);
    $('clFog').hidden = !isSound;
    $('clQuestion').textContent = t('q_' + q.type);
    renderOptions();
    renderSceneMeta();
    if (isSound) {
        $('clFogPlay').onclick = () => playSeq(q.seq, q.hz, q.rapid, $('clFogPlay'));
        $('clFogSize').textContent = t('fog_size_' + (q.L >= 200 ? 'xl' : q.L >= 75 ? 'l' : q.L >= 20 ? 'm' : 's'));
        playSeq(q.seq, q.hz, q.rapid, $('clFogPlay'));
    }
    kick(!isSound && hasFlash(q.config));
    if (!isSound) requestAnimationFrame(() => paintScene($('clScene'), q.config, q.view, q.night));
}

function renderSceneMeta() {
    const q = S.q;
    const meta = $('clMeta'), brg = $('clBearing');
    if (q.type === 'sound') { meta.textContent = ''; brg.hidden = true; return; }
    const fov = autoFov(q.config, q.view, S.metVis) * 180 / Math.PI;
    meta.textContent = t('scene_meta', {
        dist: (q.view.distance / NM).toFixed(1), eye: q.view.eye,
        fov: fov < 1 ? fov.toFixed(2) : fov.toFixed(1), horizon: horizonNm(q.view.eye).toFixed(1),
    }) + (S.metVis < 10 ? ' · ' + t('vis_meta', { n: S.metVis }) : '');
    if (q.type === 'giveway' && q.enc) {
        const e = q.enc;
        const drift = Math.abs(e.drift3);
        brg.textContent = t('own_ship2', {
            bearing: fmtBearing(e.bearing), dist: e.distNm.toFixed(1), own: e.ownKn,
            drift: q.config.makingWay ? (drift < 0.6 ? t('drift_steady') : t('drift_moves', { deg: drift.toFixed(1) })) : t('drift_anchored'),
        });
        brg.hidden = false;
    } else brg.hidden = true;
}

function fmtBearing(b) {
    const a = Math.round(Math.abs(b));
    if (a < 3) return t('bearing_ahead');
    if (a > 177) return t('bearing_astern');
    return t(b > 0 ? 'bearing_stbd' : 'bearing_port', { deg: a });
}

function optionLabel(q, o) {
    if (q.type === 'identify') return V[o.id]?.name || o.id;
    if (q.type === 'sound') return t('snd_' + o.id);
    return t('opt_' + o.id);
}

function renderOptions() {
    const box = $('clAnswers');
    box.textContent = '';
    S.q.options.forEach((o, i) => {
        const b = el('button', 'cl-ans');
        b.type = 'button';
        b.dataset.i = String(i);
        b.append(el('span', 'cl-ans-n', String(i + 1)), el('span', 'cl-ans-t', optionLabel(S.q, o)));
        if (S.exam && S.exam.answers[S.exam.i] === i) b.classList.add('picked');
        b.addEventListener('click', () => answer(i));
        box.append(b);
    });
}

function answer(i) {
    if (S.mode === 'exam') return examAnswer(i);
    if (S.answered) return;
    const opt = S.q.options[i];
    S.answered = true;
    S.stats.asked++;
    if (opt.correct) { S.stats.right++; S.stats.streak++; S.stats.best = Math.max(S.stats.best, S.stats.streak); }
    else S.stats.streak = 0;
    const skillId = S.q.type === 'sound' ? 'sound:' + S.q.signal.replace(/Large$/, '') : S.q.config.id;
    noteAnswer(S.skill, skillId, opt.correct);
    saveSkill(S.skill);

    [...$('clAnswers').children].forEach((b, k) => {
        const o = S.q.options[k];
        b.classList.add(o.correct ? 'ok' : (k === i ? 'no' : 'dim'));
        b.disabled = true;
    });
    renderVerdict(opt);
    renderStats();
    $('clNext').hidden = false;
    $('clNext').focus({ preventScroll: true });
    window.__announce?.(opt.correct ? t('right') : t('wrong'));
    funnel('answer', { type: S.q.type, id: skillId, correct: opt.correct, level: S.level });
}

function renderVerdict(opt) {
    const q = S.q, box = $('clVerdict');
    box.hidden = false;
    box.textContent = '';
    box.className = 'cl-verdict ' + (opt.correct ? 'ok' : 'no');
    box.append(el('div', 'cl-v-head', opt.correct ? t('right') : t('wrong')));
    verdictBody(q, box);
}

function verdictBody(q, box) {
    if (q.type === 'sound') {
        const line = el('div', 'cl-v-name');
        line.append(el('strong', null, SIG[q.signal]?.name || q.signal), el('span', 'cl-rule', t('rule_n', { n: ruleLabel(q.rule) })));
        box.append(line);
        if (SIG[q.signal]?.meaning) box.append(el('p', 'cl-v-hint', SIG[q.signal].meaning));
        box.append(el('p', 'cl-v-why', t('snd_pitch', { size: t('fog_size_' + (q.L >= 200 ? 'xl' : q.L >= 75 ? 'l' : q.L >= 20 ? 'm' : 's')) })));
        return;
    }
    const nameLine = el('div', 'cl-v-name');
    nameLine.append(el('strong', null, V[q.config.id]?.name || q.config.id), el('span', 'cl-rule', t('rule_n', { n: ruleLabel(q.config.rule) })));
    box.append(nameLine);
    if (V[q.config.id]?.hint) box.append(el('p', 'cl-v-hint', V[q.config.id].hint));

    const grid = el('div', 'cl-v-grid');
    const left = el('div');
    const ex = explain(q, S.metVis);
    const list = el('ul', 'cl-v-lights');
    for (const l of [...ex.lights].sort((a, b) => b.el - a.el)) {
        const li = el('li');
        li.append(el('span', 'cl-dot ' + l.color), el('span', null, t('lk_' + l.kind + '_' + l.color) || t('lk_' + l.kind)));
        list.append(li);
    }
    left.append(el('div', 'cl-v-sub', t('you_saw')), list, el('p', 'cl-v-why', whyText(ex)));
    if (q.type === 'giveway' && q.enc) {
        const v = giveWay('power', q.config, q.enc);
        left.append(el('p', 'cl-v-why cl-v-strong', t('gw_' + v.verdict) + ' — ' + t('rule_n', { n: ruleLabel(v.rule) })));
        if (v.cpa != null && q.config.makingWay) left.append(el('p', 'cl-v-why', t('gw_geometry', { cpa: v.cpa.toFixed(2), course: String(Math.round(q.enc.tgtCourse)).padStart(3, '0'), kn: q.enc.tgtKn })));
        if (RULES[v.rule]) {
            const d2 = el('details', 'cl-rule-box');
            d2.append(el('summary', null, t('rule_text', { n: ruleLabel(v.rule) })), el('p', null, RULES[v.rule]));
            left.append(d2);
        }
    } else if (RULES[q.config.rule]) {
        const d = el('details', 'cl-rule-box');
        d.append(el('summary', null, t('rule_text', { n: ruleLabel(q.config.rule) })), el('p', null, RULES[q.config.rule]));
        left.append(d);
    }
    const open = el('button', 'cl-link', t('open_in_ref'));
    open.type = 'button';
    open.addEventListener('click', () => {
        S.ref.id = q.config.id; S.ref.aspect = q.view.aspect;
        S.ref.distNm = q.view.distance / NM; S.ref.eye = q.view.eye;
        syncRefControls();
        setMode('ref');
    });
    left.append(open);
    grid.append(left);

    const plan = el('canvas', 'cl-plan');
    plan.width = 320; plan.height = 320;
    const wrap = el('div', 'cl-plan-wrap');
    wrap.append(plan, el('div', 'cl-plan-cap', t('plan_caption', { aspect: Math.round(Math.abs(q.view.aspect)), side: t(q.view.aspect >= 0 ? 'side_stbd' : 'side_port') })));
    grid.append(wrap);
    box.append(grid);
    requestAnimationFrame(() => drawPlan(plan, q.config, q.view));
}

function whyText(ex) {
    const parts = [];
    if (ex.sternOnly) parts.push(t('why_stern_only'));
    else if (ex.side === 'both') parts.push(t('why_both_sides'));
    else if (ex.side === 'stbd') parts.push(t('why_green'));
    else if (ex.side === 'port') parts.push(t('why_red'));
    if (ex.column.length >= 2) parts.push(t('why_column', { seq: ex.column.map(c => t('col_' + c.color)).join(' — ') }));
    parts.push(t('why_aspect', { deg: Math.round(Math.abs(ex.aspect)), side: t(ex.aspect >= 0 ? 'side_stbd' : 'side_port'), dist: ex.distanceNm.toFixed(1) }));
    return parts.join(' ');
}

function ruleLabel(r) { return String(r).replace(/-.*$/, ''); }

function renderStats() {
    const s = S.stats;
    $('clScore').textContent = s.asked ? `${s.right}/${s.asked}` : '—';
    $('clStreak').textContent = String(s.streak);
    $('clBest').textContent = String(s.best);
    const weak = Object.entries(S.skill).filter(([k, v]) => v.wrong > 0 && !k.startsWith('sound:')).sort((a, b) => b[1].wrong - a[1].wrong).slice(0, 4);
    const box = $('clWeak');
    box.textContent = '';
    if (!weak.length) { box.append(el('p', 'tc-hint', t('weak_none'))); return; }
    for (const [id] of weak) {
        const b = el('button', 'tc-chip', V[id]?.name || id);
        b.type = 'button';
        b.title = t('weak_open');
        b.addEventListener('click', () => { S.ref.id = id; syncRefControls(); setMode('ref'); });
        box.append(b);
    }
}

/* ═══ ЭКЗАМЕН ════════════════════════════════════════════════════════════ */
function startExam() {
    const plan = makeExam({ level: Math.max(2, S.level), night: true, metVis: 10, n: 20 });
    S.exam = { plan, i: 0, answers: new Array(plan.length).fill(null), started: Date.now(), done: false };
    $('clExamResult').hidden = true;
    $('clExamPrev').parentElement.hidden = false;
    $('clQuiz').hidden = false;
    examShow();
    clearInterval(startExam._iv);
    startExam._iv = setInterval(examClock, 1000);
    examClock();
    funnel('exam_start', {});
}

function examClock() {
    if (!S.exam || S.exam.done) return;
    const s = Math.floor((Date.now() - S.exam.started) / 1000);
    $('clExamTime').textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function examShow() {
    const e = S.exam;
    S.q = e.plan[e.i];
    $('clExamPos').textContent = t('exam_pos', { n: e.i + 1, all: e.plan.length });
    $('clExamBar').style.width = Math.round((e.answers.filter(a => a !== null).length / e.plan.length) * 100) + '%';
    showQuestion();
    $('clNext').hidden = true;
    $('clExamPrev').disabled = e.i === 0;
    $('clExamNext').textContent = e.i === e.plan.length - 1 ? t('exam_finish') : t('exam_next');
}

function examAnswer(i) {
    const e = S.exam;
    if (!e || e.done) return;
    e.answers[e.i] = i;
    [...$('clAnswers').children].forEach((b, k) => b.classList.toggle('picked', k === i));
    $('clExamBar').style.width = Math.round((e.answers.filter(a => a !== null).length / e.plan.length) * 100) + '%';
}

function examFinish() {
    const e = S.exam;
    const unanswered = e.answers.filter(a => a === null).length;
    if (unanswered && !window.confirm(t('exam_unanswered', { n: unanswered }))) return;
    e.done = true;
    clearInterval(startExam._iv);
    $('clExamPrev').parentElement.hidden = true;
    const secs = Math.floor((Date.now() - e.started) / 1000);
    let right = 0;
    const byType = {};
    const wrong = [];
    e.plan.forEach((q, k) => {
        const ok = e.answers[k] !== null && q.options[e.answers[k]].correct;
        if (ok) right++; else wrong.push(k);
        const bt = byType[q.type] || (byType[q.type] = { right: 0, all: 0 });
        bt.all++; if (ok) bt.right++;
        if (q.type !== 'sound') noteAnswer(S.skill, q.config.id, ok);
    });
    saveSkill(S.skill);
    const pct = Math.round(right / e.plan.length * 100);
    const box = $('clExamResult');
    box.textContent = '';
    box.hidden = false;
    $('clQuiz').hidden = true;
    const passed = pct >= 90;
    const ans = el('div', 'tc-answer');
    ans.append(el('p', 'tc-answer-l', t(passed ? 'exam_passed' : 'exam_failed')));
    const v = el('p', 'tc-answer-v' + (passed ? '' : ' muted'), `${right}/${e.plan.length}`);
    ans.append(v, el('p', 'tc-answer-sub', t('exam_summary', { pct, time: Math.floor(secs / 60) + ':' + String(secs % 60).padStart(2, '0') })));
    box.append(ans);
    const stats = el('div', 'tc-stats');
    for (const [ty, r] of Object.entries(byType)) {
        const f = el('div', 'tc-stat' + (r.right === r.all ? ' good' : r.right / r.all < 0.7 ? ' bad' : ' warn'));
        f.append(el('div', 'tc-stat-l', t('type_' + ty)), el('div', 'tc-stat-v', `${r.right}/${r.all}`));
        stats.append(f);
    }
    box.append(stats);
    if (wrong.length) {
        box.append(el('p', 'cl-v-sub', t('exam_review')));
        const list = el('div', 'cl-review');
        for (const k of wrong) {
            const q = e.plan[k];
            const d = el('details', 'tc-drawer');
            const sm = el('summary', null, `${k + 1}. ${t('q_' + q.type)}`);
            d.append(sm);
            const body = el('div', 'tc-drawer-body');
            const yours = e.answers[k] === null ? t('exam_no_answer') : optionLabel(q, q.options[e.answers[k]]);
            const correct = optionLabel(q, q.options.find(o => o.correct));
            body.append(el('p', 'cl-v-why', t('exam_yours', { a: yours })), el('p', 'cl-v-why cl-v-strong', t('exam_correct', { a: correct })));
            d.addEventListener('toggle', () => {
                if (!d.open || body.dataset.built) return;
                body.dataset.built = '1';
                if (q.type !== 'sound') {
                    const cv = el('canvas', 'cl-review-scene');
                    body.append(cv);
                    requestAnimationFrame(() => draw(cv, q.config, q.view, { night: q.night, metVis: 10 }));
                }
                verdictBody(q, body);
            });
            d.append(body);
            list.append(d);
        }
        box.append(list);
    }
    const acts = el('div', 'tc-actions');
    const again = el('button', 'tc-btn', t('exam_again'));
    again.type = 'button';
    again.addEventListener('click', startExam);
    acts.append(again);
    box.append(acts);
    funnel('exam_done', { right, all: e.plan.length, secs });
    renderStats();
}

/* ═══ СПРАВОЧНИК ═════════════════════════════════════════════════════════ */
const refView = () => ({ aspect: S.ref.aspect, distance: S.ref.distNm * NM, eye: S.ref.eye });

function renderRefList(filter = '') {
    const list = $('clRefList');
    list.textContent = '';
    const f = filter.trim().toLowerCase();
    const groups = {};
    for (const c of CONFIGS) {
        const name = (V[c.id]?.name || c.id);
        if (f && !name.toLowerCase().includes(f) && !(V[c.id]?.hint || '').toLowerCase().includes(f)) continue;
        (groups[c.family] ||= []).push(c);
    }
    for (const [fam, items] of Object.entries(groups)) {
        const g = el('div', 'cl-refgroup');
        g.append(el('div', 'cl-refgroup-h', t('fam_' + fam)));
        for (const c of items) {
            const b = el('button', 'cl-refitem' + (c.id === S.ref.id ? ' on' : ''));
            b.type = 'button'; b.dataset.id = c.id;
            b.append(el('span', 'cl-refitem-n', V[c.id]?.name || c.id), el('span', 'cl-refitem-r', ruleLabel(c.rule)));
            b.addEventListener('click', () => { S.ref.id = c.id; renderRef(); });
            g.append(b);
        }
        list.append(g);
    }
    if (!list.children.length) list.append(el('p', 'tc-hint', t('ref_nothing')));
}

function renderRef() {
    renderRefList($('clRefSearch').value);
    const cfg = BY_ID[S.ref.id];
    $('clRefName').textContent = V[cfg.id]?.name || cfg.id;
    $('clRefRule').textContent = t('rule_n', { n: ruleLabel(cfg.rule) });
    $('clRefDesc').textContent = V[cfg.id]?.hint || '';
    $('clRefSize').textContent = t('ref_size', { l: cfg.L, b: cfg.B });

    const ul = $('clRefLights');
    ul.textContent = '';
    const seen = new Set();
    for (const l of [...cfg.lights].sort((a, b) => b.pos[2] - a.pos[2])) {
        const key = l.kind + l.color + l.arc + l.range;
        if (seen.has(key)) continue;
        seen.add(key);
        const li = el('li');
        li.append(el('span', 'cl-dot ' + l.color), el('span', 'cl-lname', t('lk_' + l.kind + '_' + l.color) || t('lk_' + l.kind)),
            el('span', 'cl-lmeta', t('arc_' + l.arc) + ' · ' + t('range_nm', { n: l.range })));
        if (l.flash) li.append(el('span', 'cl-lmeta', t('is_flashing')));
        ul.append(li);
    }
    const sh = $('clRefShapes');
    sh.textContent = '';
    if (cfg.shapes.length) {
        sh.append(el('span', 'cl-lname', t('day_shapes') + ': '), el('span', null, cfg.shapes.map(s => (s.count > 1 ? s.count + '× ' : '') + t('shape_' + s.shape)).join(', ')));
        sh.hidden = false;
    } else sh.hidden = true;
    const vis = visibleLights(cfg, refView(), S.metVis);
    $('clRefVisible').textContent = vis.length
        ? t('visible_now', { list: [...vis].sort((a, b) => b.el - a.el).map(v => t('lk_' + v.kind + '_' + v.color) || t('lk_' + v.kind)).join(', ') })
        : t('visible_none');
    renderRefSignals(cfg);
    kick(S.ref.spin || hasFlash(cfg));
}

function syncRefAspect() {
    $('clAspect').value = String(Math.round(S.ref.aspect));
    $('clAspectOut').textContent = t('aspect_out', { deg: Math.round(Math.abs(S.ref.aspect)), side: t(S.ref.aspect >= 0 ? 'side_stbd' : 'side_port') });
}
function syncRefControls() {
    syncRefAspect();
    $('clDist').value = String(Math.round(S.ref.distNm * 10));
    $('clDistOut').textContent = t('dist_out', { n: S.ref.distNm.toFixed(1) });
    $('clEye').value = String(S.ref.eye);
    $('clEyeOut').textContent = t('eye_out', { n: S.ref.eye, h: horizonNm(S.ref.eye).toFixed(1) });
}

function renderRefSignals(cfg) {
    const box = $('clRefSound');
    box.textContent = '';
    const keys = [];
    const rv = cfg.sound.rv === 'anchorBell' && cfg.L >= 100 ? 'anchorBellLarge' : cfg.sound.rv;
    if (rv && SIGNALS[rv]) keys.push(rv);
    if (cfg.sound.identity && SIGNALS[cfg.sound.identity]) keys.push(cfg.sound.identity);
    if (!keys.length) { box.hidden = true; return; }
    box.hidden = false;
    box.append(el('div', 'cl-v-sub', t('sound_in_fog')));
    for (const k of keys) {
        const row = el('div', 'cl-sigrow');
        row.append(el('span', 'cl-signame', SIG[k]?.name || k), blastDiagram(SIGNALS[k].seq));
        const b = el('button', 'tc-btn ghost sm', t('play'));
        b.type = 'button';
        b.addEventListener('click', () => playKey(k, cfg.L, b));
        row.append(b);
        box.append(row);
    }
}

/* Перетаскивание сцены справочника: ракурс под пальцем, дистанция — колесом. */
function bindRefDrag() {
    const cv = $('clRefScene');
    cv.addEventListener('pointerdown', e => {
        S.drag = { x: e.clientX, a: S.ref.aspect };
        S.ref.spin = false; $('clSpin').checked = false;
        cv.setPointerCapture?.(e.pointerId);
        kick(true);
    });
    cv.addEventListener('pointermove', e => {
        if (!S.drag) return;
        const dx = e.clientX - S.drag.x;
        S.ref.aspect = ((S.drag.a - dx * 0.6 + 180) % 360 + 360) % 360 - 180;
        syncRefAspect();
    });
    const end = () => { if (S.drag) { S.drag = null; renderRef(); funnel('ref_drag', {}); } };
    cv.addEventListener('pointerup', end);
    cv.addEventListener('pointercancel', end);
    cv.addEventListener('wheel', e => {
        e.preventDefault();
        S.ref.distNm = Math.max(0.2, Math.min(12, S.ref.distNm * (e.deltaY > 0 ? 1.1 : 0.9)));
        syncRefControls(); renderRef();
    }, { passive: false });
}

/* ═══ СИГНАЛЫ ════════════════════════════════════════════════════════════ */
function blastDiagram(seq) {
    const d = el('span', 'cl-blast');
    d.setAttribute('aria-hidden', 'true');
    for (const s of seq) {
        if (s === 'S') d.append(el('i', 'b-short'));
        else if (s === 'P') d.append(el('i', 'b-long'));
        else if (s.startsWith('GONG')) d.append(el('i', 'b-gong'));
        else d.append(el('i', 'b-bell'));
    }
    return d;
}

function playSeq(seq, hz, rapid, btn) {
    unlockAudio();
    S.audio?.stop();
    btn?.classList.add('playing');
    S.audio = playSignal(seq, { hz, rapid });
    setTimeout(() => btn?.classList.remove('playing'), S.audio?.durationMs || 0);
}

function playKey(key, lengthM, btn) {
    const sig = SIGNALS[key];
    if (!sig) return;
    playSeq(sig.seq, whistleHz(lengthM ?? 90), !!sig.rapid, btn);
    funnel('signal_play', { key });
}

function renderSignals() {
    const box = $('clSigList');
    if (box.dataset.built) return;
    const groups = { '34': [], '35': [] };
    for (const [k, v] of Object.entries(SIGNALS)) (v.rule.startsWith('34') ? groups['34'] : groups['35']).push([k, v]);
    for (const [g, items] of Object.entries(groups)) {
        const sec = el('section', 'cl-siggroup');
        sec.append(el('h3', 'tc-title', t('sig_group_' + g)), el('p', 'tc-hint', t('sig_group_' + g + '_d')));
        const grid = el('div', 'cl-siggrid');
        for (const [k, v] of items) {
            const row = el('div', 'cl-sigcard');
            const head = el('div', 'cl-sigcard-h');
            head.append(el('span', 'cl-signame', SIG[k]?.name || k), el('span', 'cl-rule', ruleLabel(v.rule)));
            row.append(head, blastDiagram(v.seq));
            if (SIG[k]?.meaning) row.append(el('p', 'cl-sigmean', SIG[k].meaning));
            const b = el('button', 'tc-btn ghost sm', t('play'));
            b.type = 'button';
            b.addEventListener('click', () => playKey(k, Number($('clSigSize').value), b));
            row.append(b);
            grid.append(row);
        }
        sec.append(grid);
        box.append(sec);
    }
    box.dataset.built = '1';
}

/* ═══ ПЕЧАТНЫЕ КАРТОЧКИ (Премиум) ════════════════════════════════════════ */
/** Карточка рисует судно ВНУТРИ дальности его огней: бортовые малого судна видны на милю,
    и с 1.2 мили карточка честно показала бы пустую ночь. */
const cardDistance = c => Math.min(1.2, 0.7 * Math.min(...c.lights.map(l => l.range || 1.2)));

async function makeCards() {
    if (!isPro()) {
        window.__toast?.(t('cards_locked'), 'info');
        const n = $('clProNote');
        n.hidden = false;
        n.textContent = '';
        n.append(document.createTextNode(t('cards_locked') + ' '));
        const a = el('a', 'tc-perk', t('pro_link'));
        a.href = B.cfg.proUrl;
        n.append(a);
        funnel('perk_upsell', { what: 'cards' });
        return;
    }
    const host = $('clCardsHost');
    host.textContent = '';
    host.hidden = false;
    /* Страницы по шесть карточек с явным разрывом: разрыв «по месту» в сетке из двух
       колонок резал карточку пополам между листами. */
    const sheet = el('div');
    let page = null;
    CONFIGS.forEach((c, n) => {
        if (n % 6 === 0) {
            if (page) sheet.append(el('div', 'html2pdf__page-break'));
            page = el('div', 'cl-cards');
            sheet.append(page);
        }
        const card = el('div', 'cl-card-print');
        const cv = el('canvas');
        card.append(cv);
        card.append(el('b', null, V[c.id]?.name || c.id));
        card.append(el('span', null, t('rule_n', { n: ruleLabel(c.rule) })));
        if (c.shapes.length) card.append(el('span', null, t('day_shapes') + ': ' + c.shapes.map(s => (s.count > 1 ? s.count + '× ' : '') + t('shape_' + s.shape)).join(', ')));
        if (c.sound.rv && SIG[c.sound.rv]) card.append(el('span', null, t('sound_in_fog') + ': ' + SIG[c.sound.rv].name));
        page.append(card);
    });
    host.append(sheet);
    await new Promise(r => requestAnimationFrame(r));
    [...sheet.querySelectorAll('canvas')].forEach((cv, k) => draw(cv, CONFIGS[k], { aspect: 32, distance: cardDistance(CONFIGS[k]) * NM, eye: 12 }, { night: true, metVis: 10 }));
    funnel('cards_pdf', {});
    /* Холсты нарисованы на прикреплённом листе; примитиву он отдаётся откреплённым —
       прикреплённый узел с position:fixed схлопывает контейнер html2pdf. */
    sheet.remove();
    try {
        const res = await window.__generatePDF?.({ element: sheet, asIs: true, filename: 'colreg-lights-cards.pdf', pagebreak: { mode: ['legacy'] } });
        if (res && res.ok === false) window.__toast?.(t('pdf_error'), 'error');
    } finally { host.hidden = true; host.textContent = ''; }
}

/* ── подключение ─────────────────────────────────────────────────────────── */
function bind() {
    for (const b of document.querySelectorAll('.cl-mode')) b.addEventListener('click', () => setMode(b.dataset.mode));
    $('clNext').addEventListener('click', nextQuestion);

    for (const b of document.querySelectorAll('.cl-level')) {
        b.addEventListener('click', () => {
            S.level = Number(b.dataset.v);
            for (const x of document.querySelectorAll('.cl-level')) x.classList.toggle('on', x === b);
            store.save('level', S.level);
            nextQuestion();
        });
    }
    for (const c of document.querySelectorAll('.cl-type')) {
        c.addEventListener('click', () => {
            c.classList.toggle('on');
            const on = [...document.querySelectorAll('.cl-type.on')].map(x => x.dataset.v);
            if (!on.length) { c.classList.add('on'); return; }
            S.types = on;
            for (const x of document.querySelectorAll('.cl-type')) x.setAttribute('aria-pressed', x.classList.contains('on') ? 'true' : 'false');
            store.save('types', S.types);
            nextQuestion();
        });
    }
    $('clNight').addEventListener('change', e => { S.night = e.target.checked; $('clRefNight').checked = S.night; store.save('night', S.night); if (S.mode === 'train') nextQuestion(); else renderRef(); });
    $('clSil').addEventListener('change', e => { S.silhouette = e.target.checked; store.save('sil', S.silhouette); kick(needsAnim); if (S.mode === 'train' && S.q?.config) paintScene($('clScene'), S.q.config, S.q.view, S.q.night); });
    $('clVis').addEventListener('input', e => {
        S.metVis = Number(e.target.value);
        $('clVisOut').textContent = S.metVis >= 10 ? t('vis_good') : t('vis_out', { n: S.metVis });
        store.save('vis', S.metVis);
    });
    $('clVis').addEventListener('change', () => { if (S.mode === 'train') nextQuestion(); else renderRef(); });

    $('clExamPrev').addEventListener('click', () => { if (S.exam.i > 0) { S.exam.i--; examShow(); } });
    $('clExamNext').addEventListener('click', () => { if (S.exam.i < S.exam.plan.length - 1) { S.exam.i++; examShow(); } else examFinish(); });

    $('clAspect').addEventListener('input', e => { S.ref.spin = false; $('clSpin').checked = false; S.ref.aspect = Number(e.target.value); syncRefAspect(); renderRef(); });
    $('clDist').addEventListener('input', e => { S.ref.distNm = Number(e.target.value) / 10; syncRefControls(); renderRef(); });
    $('clEye').addEventListener('input', e => { S.ref.eye = Number(e.target.value); syncRefControls(); renderRef(); });
    $('clSpin').addEventListener('change', e => { S.ref.spin = e.target.checked; kick(true); });
    $('clRefNight').addEventListener('change', e => { S.night = e.target.checked; $('clNight').checked = S.night; store.save('night', S.night); renderRef(); });
    let sd;
    $('clRefSearch').addEventListener('input', () => { clearTimeout(sd); sd = setTimeout(() => renderRefList($('clRefSearch').value), 120); });
    bindRefDrag();
    $('clCards').addEventListener('click', makeCards);

    document.addEventListener('keydown', e => {
        if (S.mode !== 'train' && S.mode !== 'exam') return;
        if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : (e.target.matches('input,textarea,select'))) return;
        if (e.key >= '1' && e.key <= '6' && (!S.answered || S.mode === 'exam')) {
            const b = $('clAnswers').children[Number(e.key) - 1];
            if (b) { b.click(); e.preventDefault(); }
        } else if (S.mode === 'train' && S.answered && (e.key === 'Enter' || e.key === ' ')) { nextQuestion(); e.preventDefault(); }
        else if (S.mode === 'exam' && (e.key === 'Enter' || e.key === 'ArrowRight')) { $('clExamNext').click(); e.preventDefault(); }
        else if (S.mode === 'exam' && e.key === 'ArrowLeft') { $('clExamPrev').click(); e.preventDefault(); }
    });
    let rt;
    window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => kick(needsAnim), 120); });
}

function restore() {
    S.level = Number(store.load('level', 1)) || 1;
    const ty = store.load('types', ['identify']);
    S.types = Array.isArray(ty) && ty.length ? ty.filter(x => ['identify', 'aspect', 'giveway', 'sound'].includes(x)) : ['identify'];
    if (!S.types.length) S.types = ['identify'];
    S.night = store.load('night', true) !== false;
    S.silhouette = store.load('sil', false) === true;
    S.metVis = Number(store.load('vis', 10)) || 10;
    S.skill = loadSkill();
    for (const x of document.querySelectorAll('.cl-level')) x.classList.toggle('on', Number(x.dataset.v) === S.level);
    for (const c of document.querySelectorAll('.cl-type')) { const on = S.types.includes(c.dataset.v); c.classList.toggle('on', on); c.setAttribute('aria-pressed', on ? 'true' : 'false'); }
    $('clNight').checked = S.night; $('clRefNight').checked = S.night;
    $('clSil').checked = S.silhouette;
    $('clVis').value = String(S.metVis);
    $('clVisOut').textContent = S.metVis >= 10 ? t('vis_good') : t('vis_out', { n: S.metVis });
    syncRefControls();
}

function boot() {
    try {
        bind();
        restore();
        renderStats();
        setMode('train');
        window.__stage?.('ready');
    } catch (err) {
        window.__handleError?.(err, 'colreg-lights');
        const box = $('clQuestion');
        if (box) box.textContent = t('load_error');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
